import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import axios from 'axios';
import { API } from '@/App';
import { SectionHomework } from '@/components/SectionHomework';
import { WorksheetSlot } from '@/components/worksheets/WorksheetSlot';
import { ReflectOnThis } from '@/components/companion/ReflectOnThis';
import { SupportNotice } from '@/components/support/SupportResources';
import { ArrowLeft, CheckCircle, Play, FileText, Link as LinkIcon, Headphones, Lock, Clock, Folder, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';
import { MarkdownWithLinks } from '@/components/MarkdownWithLinks';
import { useAudio } from '@/contexts/AudioContext';
import TruncatedText from '@/components/TruncatedText';
import PdfViewer from '@/components/PdfViewer';
import TierPaymentModal from '@/components/TierPaymentModal';
import DripPacingOffer from '@/components/DripPacingOffer';
import { BaselineGate, EndOfModuleGate } from '@/components/AssessmentGates';
import { useTranslatedMap } from '@/contexts/LanguageContext';
import { UnlockCountdown } from '@/components/UnlockCountdown';

const ModuleDetailPage = ({ user, moduleId: moduleIdProp, onBack }) => {
  const params = useParams();
  // Normally the id comes from the route. Platinum+ mounts this same reader inside the
  // sanctuary and hands the id in directly, so there is one reader, not two.
  const moduleId = moduleIdProp || params.moduleId;
  const navigate = useNavigate();
  const location = useLocation();

  const { fetchAndPlayMusic, pauseForOtherAudio, resumeAfterOtherAudio } = useAudio();
  
  const [module, setModule] = useState(null);
  const [folders, setFolders] = useState([]);
  const [contents, setContents] = useState([]);
  const [folderAccess, setFolderAccess] = useState({});
  const [completedContents, setCompletedContents] = useState(new Set());
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [completedFolders, setCompletedFolders] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [moduleAccess, setModuleAccess] = useState(null);
  const tr = useTranslatedMap([
    module?.title, module?.description,
    ...contents.flatMap((c) => [c.title, c.content_data?.text, c.content_data?.instructions, c.content_data?.description]),
    ...folders.flatMap((f) => [f.name, f.description]),
  ]);
  const [membershipTier, setMembershipTier] = useState('bronze');
  const [showTierPaymentModal, setShowTierPaymentModal] = useState(false);
  const [gates, setGates] = useState(null);
  const [adminBypass, setAdminBypass] = useState(false);
  const [moduleCompleted, setModuleCompleted] = useState(false);
  const [startingAssessment, setStartingAssessment] = useState(false);
  const [showDripOffer, setShowDripOffer] = useState(false);
  const [tierLocked, setTierLocked] = useState(false);
  
  const videoRefs = useRef([]);
  const openedFolders = useRef(new Set());
  const audioRefs = useRef([]);

  const fetchModuleData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [moduleRes, foldersRes, contentsRes, contentCompletionsRes, folderCompletionsRes, accessRes, membershipRes, gatesRes, completedModulesRes] = await Promise.all([
        axios.get(`${API}/modules/${moduleId}`, { headers }),
        axios.get(`${API}/content-folders/${moduleId}`, { headers }),
        axios.get(`${API}/module-contents/${moduleId}`, { headers }),
        axios.get(`${API}/content/completed`, { headers }),
        axios.get(`${API}/folders/completed`, { headers }),
        axios.get(`${API}/modules/${moduleId}/access`, { headers }).catch(() => ({ data: { has_access: true } })),
        axios.get(`${API}/user/membership`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/assessments/module/${moduleId}/gates`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/modules/completed`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setModule(moduleRes.data);
      setModuleAccess(accessRes.data);
      setGates(gatesRes.data);
      setModuleCompleted((completedModulesRes.data || []).includes(moduleId));
      setMembershipTier(membershipRes.data?.membership_tier || 'bronze');
      // Module 2 is where paced release first bites — offer the one-time alternative there.
      if (moduleRes.data?.order === 2) {
        axios.get(`${API}/user/drip-prompt`, { headers })
          .then((r) => setShowDripOffer(Boolean(r.data?.show)))
          .catch(() => {});
      }
      setFolders(foldersRes.data.sort((a, b) => a.order - b.order));
      setContents(contentsRes.data);
      
      // Build set of completed content IDs
      const completedContentSet = new Set(contentCompletionsRes.data.map(c => c.content_id));
      setCompletedContents(completedContentSet);
      
      // Build set of completed folder IDs
      const completedFolderSet = new Set(folderCompletionsRes.data.map(f => f.folder_id));
      setCompletedFolders(completedFolderSet);
      
      // Fetch access status for each folder
      const accessPromises = foldersRes.data.map(folder => 
        axios.get(`${API}/folders/${folder.id}/access`, { headers })
          .then(res => ({ [folder.id]: res.data }))
          .catch(() => ({ [folder.id]: { accessible: false, reason: 'Error checking access' } }))
      );
      
      const accessResults = await Promise.all(accessPromises);
      const accessMap = Object.assign({}, ...accessResults);
      setFolderAccess(accessMap);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching module data:', error);
      if (error?.response?.status === 403) {
        setTierLocked(true);
      } else {
        toast.error('Failed to load module data');
      }
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchModuleData();
      // NO background music in modules - stop any playing music
      pauseForOtherAudio();
    }, 0);
    return () => clearTimeout(timer);
  }, [fetchModuleData, pauseForOtherAudio]);

  // Opening a folder is what starts the drip clock for the next one. Idempotent server-side, so
  // re-opening never restarts it — we just fire once per session and refresh the countdowns.
  const markFolderOpened = async (folderId) => {
    if (openedFolders.current.has(folderId)) return;
    openedFolders.current.add(folderId);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const res = await axios.post(`${API}/folders/${folderId}/open`, {}, { headers });
      if (!res.data?.already_open) {
        // A fresh clock just started — pull the neighbouring folders' unlock dates in
        const results = await Promise.all(
          folders.map((f) =>
            axios.get(`${API}/folders/${f.id}/access`, { headers })
              .then((r) => ({ [f.id]: r.data }))
              .catch(() => ({}))
          )
        );
        setFolderAccess((prev) => Object.assign({}, prev, ...results));
      }
    } catch (e) {
      openedFolders.current.delete(folderId);
    }
  };


  const handleCompleteContent = async (contentId) => {    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/content/${contentId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletedContents(prev => new Set([...prev, contentId]));
      toast.success('Content marked as complete!');
    } catch (error) {
      toast.error('Failed to mark content as complete');
    }
  };

  const handleCompleteFolder = async (folderId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/folders/${folderId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCompletedFolders(prev => new Set([...prev, folderId]));
      toast.success('Folder marked as complete!');
      
      // Refresh to update access for next folder
      fetchModuleData();
    } catch (error) {
      toast.error('Failed to mark folder as complete');
    }
  };

  // His design: the final complete button in the module opens the end-of-module assessment,
  // and submitting that assessment is what writes the module completion.
  const handleStartEndAssessment = async (folderId, folderCompleted, assessmentId) => {
    setStartingAssessment(true);
    try {
      if (!folderCompleted) {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/folders/${folderId}/complete`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate(`/dashboard/assessments/${assessmentId}/take`);
    } catch (error) {
      toast.error('Could not open the assessment');
      setStartingAssessment(false);
    }
  };

  // Modules with no published end-of-module assessment still need a completion, otherwise
  // the next module stays locked forever.
  const handleCompleteModule = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/modules/${moduleId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuleCompleted(true);
      toast.success('Module marked as complete!');
    } catch (error) {
      toast.error('Failed to complete the module');
    }
  };

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'video': return <Play className="w-5 h-5" />;
      case 'meditation': return <Headphones className="w-5 h-5" />;
      case 'blog': return <FileText className="w-5 h-5" />;
      case 'exercise': return <FileText className="w-5 h-5" />;
      case 'supplement': return <FileText className="w-5 h-5" />;
      case 'link': return <LinkIcon className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const convertToEmbedUrl = (url) => {
    if (!url) return url;
    
    // Convert YouTube watch URLs to embed URLs
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Convert youtu.be short URLs
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Already an embed URL or other format
    return url;
  };

  const renderContent = (content) => {
    const isCompleted = completedContents.has(content.id);
    
    return (
      <div key={content.id} className="border rounded-lg p-4 bg-white">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <div className="text-cyan-600">
              {getContentIcon(content.content_type)}
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-lg">{tr(content.title)}</h4>
              <span className="text-sm text-gray-500 capitalize">{content.content_type}</span>
            </div>
          </div>
          {isCompleted && (
            <CheckCircle className="w-6 h-6 text-green-500" />
          )}
        </div>

        {/* Content Display */}
        <div className="mb-3">
          {content.content_type === 'video' && (
            <>
              {/* YouTube/Vimeo URL */}
              {content.content_data?.video_url && (
                <div className="aspect-video bg-black rounded">
                  <iframe
                    src={convertToEmbedUrl(content.content_data.video_url)}
                    className="w-full h-full rounded"
                    allowFullScreen
                  />
                </div>
              )}

              {/* Uploaded MP4 with YouTube-style thumbnail */}
              {content.content_data?.video_file_url && (
                <div className="relative aspect-video bg-black rounded overflow-hidden group">
                  <video
                    className="w-full h-full"
                    controls
                    poster={content.content_data?.thumbnail_url}
                    onPlay={() => {
                      setPlayingVideos(prev => new Set([...prev, content.id]));
                    }}
                    onPause={() => {
                      setPlayingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(content.id);
                        return newSet;
                      });
                    }}
                    onEnded={() => {
                      setPlayingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(content.id);
                        return newSet;
                      });
                    }}
                  >
                    <source src={content.content_data.video_file_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Custom play button overlay - only shows when video hasn't started */}
                  {content.content_data?.thumbnail_url && !playingVideos.has(content.id) && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl opacity-90 group-hover:opacity-100 transition-opacity">
                        <div className="w-0 h-0 ml-1 border-t-[15px] border-t-transparent border-l-[25px] border-l-white border-b-[15px] border-b-transparent"></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Text Content */}
          {content.content_type === 'text' && (
            <div>
              {/* Display Unsplash photo if available */}
              {(content.content_data?.ai_image || content.ai_image) && (
                <div className="mb-4">
                  <img 
                    src={content.content_data?.ai_image || content.ai_image}
                    alt={content.title}
                    className="w-full h-48 md:h-64 lg:h-96 object-cover rounded-lg"
                  />
                  {content.content_data?.image_attribution && (
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      Photo by{' '}
                      <a 
                        href={content.content_data.image_attribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        {content.content_data.image_attribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href="https://unsplash.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              {content.content_data?.text && (
                <div className="prose max-w-none">
                  <MarkdownWithLinks>{tr(content.content_data.text)}</MarkdownWithLinks>
                </div>
              )}
            </div>
          )}

          {/* Supplement Content */}
          {content.content_type === 'supplement' && (
            <div>
              {/* Display Unsplash photo if available */}
              {(content.content_data?.ai_image || content.ai_image) && (
                <div className="mb-4">
                  <img 
                    src={content.content_data?.ai_image || content.ai_image}
                    alt={content.title}
                    className="w-full h-48 md:h-64 lg:h-96 object-cover rounded-lg"
                  />
                  {content.content_data?.image_attribution && (
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      Photo by{' '}
                      <a 
                        href={content.content_data.image_attribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        {content.content_data.image_attribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href="https://unsplash.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-600 hover:underline"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              {content.content_data?.text && (
                <div className="prose max-w-none">
                  <MarkdownWithLinks>{tr(content.content_data.text)}</MarkdownWithLinks>
                </div>
              )}
              {/* A published worksheet replaces the PDF viewer; the original stays a download */}
              <WorksheetSlot
                contentId={content.id}
                originalUrl={content.content_data?.file_url
                  || content.content_data?.pdf_url
                  || content.content_data?.document_url}
              >
                {content.content_data?.file_url ? (
                  <div className="mt-4">
                    <PdfViewer url={content.content_data.file_url} title={content.title} />
                  </div>
                ) : content.content_data?.pdf_url ? (
                  <PdfViewer url={content.content_data.pdf_url} title={content.title} />
                ) : content.content_data?.document_url ? (
                  <PdfViewer url={content.content_data.document_url} title={content.title} />
                ) : content.content_data?.description ? (
                  <div className="prose max-w-none">
                    <MarkdownWithLinks>{tr(content.content_data.description)}</MarkdownWithLinks>
                  </div>
                ) : null}
              </WorksheetSlot>
              <div className="mt-4">
                <ReflectOnThis contentId={content.id} title={content.title} kind="supplement" />
              </div>
            </div>
          )}

          {content.content_type === 'meditation' && (
            <div>
              {/* Display Unsplash photo if available */}
              {(content.content_data?.ai_image || content.ai_image) && (
                <div className="mb-4">
                  <img 
                    src={content.content_data?.ai_image || content.ai_image}
                    alt={content.title}
                    className="w-full h-48 md:h-64 lg:h-96 object-cover rounded-lg"
                  />
                  {content.content_data?.image_attribution && (
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      Photo by{' '}
                      <a 
                        href={content.content_data.image_attribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        {content.content_data.image_attribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href={content.content_data.image_attribution.unsplash_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              {/* Audio player - check both file_url and audio_url */}
              {(content.content_data?.file_url || content.content_data?.audio_url) && (
                <audio controls className="w-full">
                  <source src={content.content_data?.file_url || content.content_data?.audio_url} />
                </audio>
              )}
            </div>
          )}

          {content.content_type === 'blog_audio' && (
            <div>
              {/* Display Unsplash photo if available */}
              {(content.content_data?.ai_image || content.ai_image) && (
                <div className="mb-4">
                  <img 
                    src={content.content_data?.ai_image || content.ai_image}
                    alt={content.title}
                    className="w-full h-48 md:h-64 lg:h-96 object-cover rounded-lg"
                  />
                  {content.content_data?.image_attribution && (
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      Photo by{' '}
                      <a 
                        href={content.content_data.image_attribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        {content.content_data.image_attribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href={content.content_data.image_attribution.unsplash_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              {/* Audio player labeled as "Audio" for Additional Audio type */}
              {(content.content_data?.file_url || content.content_data?.audio_url) && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Audio</h4>
                  <audio controls className="w-full mb-4">
                    <source src={content.content_data?.file_url || content.content_data?.audio_url} />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
              {/* Show notes/transcript */}
              {content.content_data?.instructions && (
                <div className="prose prose-lg max-w-none">
                  <TruncatedText text={content.content_data.instructions} maxLength={500} />
                </div>
              )}
            </div>
          )}

          {content.content_type === 'blog' && (
            <div>
              {/* Display Unsplash photo if available */}
              {(content.content_data?.ai_image || content.ai_image) && (
                <div className="mb-4">
                  <img 
                    src={content.content_data?.ai_image || content.ai_image}
                    alt={content.title}
                    className="w-full h-48 md:h-64 lg:h-96 object-cover rounded-lg"
                  />
                  {content.content_data?.image_attribution && (
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      Photo by{' '}
                      <a 
                        href={content.content_data.image_attribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        {content.content_data.image_attribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href={content.content_data.image_attribution.unsplash_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              {content.content_data?.pdf_url ? (
                <PdfViewer url={content.content_data.pdf_url} title={content.title} />
              ) : content.content_data?.document_url ? (
                <PdfViewer url={content.content_data.document_url} title={content.title} />
              ) : content.content_data?.text ? (
                <div className="prose max-w-none">
                  <MarkdownWithLinks>{tr(content.content_data.text)}</MarkdownWithLinks>
                </div>
              ) : null}
              
              {/* Blog/Audio player - if audio was added */}
              {content.content_data?.blog_audio_url && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Blog/Audio</h4>
                  <audio controls className="w-full">
                    <source src={content.content_data.blog_audio_url} />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}
            </div>
          )}

          {['exercise', 'check_in', 'reflection'].includes(content.content_type) && (
            <div>
              {/* Display Unsplash photo if available */}
              {(content.content_data?.ai_image || content.ai_image) && (
                <div className="mb-4">
                  <img 
                    src={content.content_data?.ai_image || content.ai_image}
                    alt={content.title}
                    className="w-full h-48 md:h-64 lg:h-96 object-cover rounded-lg"
                  />
                  {content.content_data?.image_attribution && (
                    <p className="text-xs text-gray-500 mt-2 text-right">
                      Photo by{' '}
                      <a 
                        href={content.content_data.image_attribution.photographer_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        {content.content_data.image_attribution.photographer}
                      </a>
                      {' '}on{' '}
                      <a 
                        href={content.content_data.image_attribution.unsplash_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        Unsplash
                      </a>
                    </p>
                  )}
                </div>
              )}
              {content.content_type === 'check_in' && (
                <p className="text-sm text-cyan-800 bg-cyan-50 border border-cyan-200 rounded-lg px-4 py-3 mb-4"
                   data-testid="check-in-note">
                  This is a check-in, not an assessment — a gentle pause to notice what has
                  shifted. What you write stays yours, and it gathers in your Companion under
                  Values, Beliefs &amp; Intentions.
                </p>
              )}
              {/* A published worksheet replaces the PDF viewer; the original stays a download */}
              <WorksheetSlot
                contentId={content.id}
                originalUrl={content.content_data?.pdf_url || content.content_data?.document_url}
              >
                {content.content_data?.pdf_url ? (
                  <PdfViewer url={content.content_data.pdf_url} title={content.title} />
                ) : content.content_data?.document_url ? (
                  <PdfViewer url={content.content_data.document_url} title={content.title} />
                ) : content.content_data?.instructions ? (
                  <div className="prose max-w-none">
                    <MarkdownWithLinks>{tr(content.content_data.instructions)}</MarkdownWithLinks>
                  </div>
                ) : null}
              </WorksheetSlot>
              <div className="mt-4">
                <ReflectOnThis contentId={content.id} title={content.title} kind="exercise" />
              </div>
            </div>
          )}

          {content.content_type === 'link' && content.content_data?.url && (
            <a
              href={content.content_data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-600 hover:underline flex items-center gap-2"
            >
              <LinkIcon className="w-4 h-4" />
              {content.content_data.url}
            </a>
          )}
        </div>

        {/* Complete Button */}
        {!isCompleted && (
          <Button
            onClick={() => handleCompleteContent(content.id)}
            className="w-full bg-gradient-to-r from-cyan-500 to-purple-500"
          >
            Mark as Complete
          </Button>
        )}
      </div>
    );
  };

  const renderFolder = (folder, isLastFolder) => {
    const access = folderAccess[folder.id] || { accessible: false, reason: 'Checking...' };
    const isCompleted = completedFolders.has(folder.id);
    const folderContents = contents.filter(c => c.folder_id === folder.id).sort((a, b) => a.order - b.order);
    const isExpanded = expandedFolders[folder.id];
    const endGate = isLastFolder ? gates?.end : null;
    
    // Check if all content in folder is completed
    const allContentCompleted = folderContents.length > 0 && folderContents.every(c => completedContents.has(c.id));

    return (
      <Card key={folder.id} className={`${!access.accessible ? 'opacity-60' : ''}`}>
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => {
            if (access.accessible) {
              setExpandedFolders(prev => ({ ...prev, [folder.id]: !prev[folder.id] }));
              markFolderOpened(folder.id);
            }
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1">
              {access.accessible ? (
                <FolderOpen className="w-6 h-6 text-cyan-600" />
              ) : (
                <Lock className="w-6 h-6 text-gray-400" />
              )}
              <div className="flex-1">
                <CardTitle className="text-xl flex items-center gap-2">
                  {folder.order}. {tr(folder.name)}
                  {isCompleted && <CheckCircle className="w-5 h-5 text-green-500" />}
                </CardTitle>
                {folder.description && (
                  <p className="text-sm text-gray-600 mt-1">{tr(folder.description)}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {folderContents.length} item{folderContents.length !== 1 ? 's' : ''}
              </span>
              {!access.accessible && access.unlock_date && (
                <UnlockCountdown
                  unlockDate={access.unlock_date}
                  compact
                  tone="light"
                  testId={`folder-countdown-${folder.id}`}
                />
              )}
              {!access.accessible && !access.unlock_date && folder.days_delay > 0 && (
                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {folder.days_delay} day timer
                </span>
              )}
            </div>
          </div>

          {!access.accessible && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-center justify-between gap-3">
              <span>🔒 {access.reason}</span>
              {access.unlock_date && (
                <span className="text-amber-900 whitespace-nowrap font-medium">
                  {new Date(access.unlock_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              )}
            </div>
          )}

          {access.accessible && folder.days_delay > 0 && !isLastFolder && (
            <div className="mt-3 text-xs text-gray-500">
              Opening this folder starts the {folder.days_delay}-day timer for the next one — take
              your time inside, you won't lose the head start.
            </div>
          )}
        </CardHeader>

        {access.accessible && isExpanded && (
          <CardContent className="space-y-4">
            {folderContents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No content in this folder yet</p>
            ) : (
              <>
                {folderContents.map(content => renderContent(content))}
                
                {/* Folder Complete Button — suppressed on the last folder when an
                    end-of-module assessment is the completion action instead. */}
                {!isCompleted && allContentCompleted && !endGate && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={() => handleCompleteFolder(folder.id)}
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                      size="lg"
                      data-testid={`folder-complete-btn-${folder.id}`}
                    >
                      ✓ Mark Folder as Complete
                    </Button>
                  </div>
                )}

                {allContentCompleted && endGate && (
                  <EndOfModuleGate
                    gate={endGate}
                    starting={startingAssessment}
                    onStart={() => handleStartEndAssessment(folder.id, isCompleted, endGate.assessment_id)}
                  />
                )}

                {/* No assessment at this location yet — the module still has to be completable. */}
                {isLastFolder && !endGate && isCompleted && !moduleCompleted && (
                  <div className="pt-4 border-t">
                    <Button
                      onClick={handleCompleteModule}
                      className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 text-white"
                      size="lg"
                      data-testid="module-complete-btn"
                    >
                      Mark Module as Complete
                    </Button>
                    <p className="text-center text-xs text-gray-500 mt-2">
                      This unlocks the next module.
                    </p>
                  </div>
                )}

                {!allContentCompleted && (
                  <div className="pt-4 border-t text-center text-sm text-gray-500">
                    Complete all items above to mark this folder as complete
                  </div>
                )}
              </>
            )}
            <SectionHomework folderId={folder.id} folderName={folder.name} isAdmin={user?.role === 'admin'} />
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading module...</div>
      </div>
    );
  }

  if (tierLocked) {
    return (
      <div className="max-w-xl mx-auto py-24 px-6" data-testid="module-tier-locked">
        <div className="rounded-2xl border border-white/10 bg-slate-950/70 backdrop-blur-xl px-8 py-12 text-center shadow-2xl">
          <Lock className="w-10 h-10 mx-auto mb-5 text-amber-400" />
          <h1 className="text-3xl font-semibold mb-3 text-white">This module isn't in your membership yet</h1>
          <p className="text-base text-slate-300 mb-8">
            Upgrade your membership to unlock this part of the coaching program.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate('/dashboard/modules')}
              className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"
              data-testid="module-locked-back-btn">Back to Modules</Button>
            <Button onClick={() => navigate(`/dashboard/membership?from=${encodeURIComponent(location.pathname)}&from_label=${encodeURIComponent('this module')}`)}
              data-testid="module-locked-upgrade-btn">View Membership Options</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Module not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {(!moduleIdProp || onBack) && (
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => (onBack ? onBack() : navigate('/dashboard/modules'))}
          className="bg-white hover:bg-gray-50"
          data-testid="module-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {onBack ? 'Back to Training' : 'Back to Modules'}
        </Button>
      </div>
      )}

      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">{tr(module.title)}</h1>
        <p className="text-gray-300 text-lg">{tr(module.description)}</p>
      </div>

      {/* A check-in where the work goes deepest. Set per module by administration. */}
      {module.show_support_notice && <SupportNotice className="mb-6" />}

      {moduleAccess && moduleAccess.has_access === false && moduleAccess.reason === 'tier_insufficient' && (
        <Card
          className="border border-amber-300/25 bg-white/5 backdrop-blur-xl shadow-[0_8px_40px_rgba(0,0,0,0.35)] mb-6"
          data-testid="module-tier-upgrade-banner"
        >
          <CardContent className="p-6 flex flex-col sm:flex-row items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-5 h-5 text-amber-300" />
                <h3 className="text-lg md:text-xl font-semibold text-white">Upgrade to Unlock This Module</h3>
              </div>
              <p className="text-sm md:text-base text-gray-300">
                This module is part of a higher membership tier. You can preview what's inside below — upgrade your membership to unlock the full content.
              </p>
            </div>
            <Button
              onClick={() => setShowTierPaymentModal(true)}
              className="bg-amber-400 text-slate-900 transition-colors duration-200 hover:bg-amber-300 whitespace-nowrap"
              data-testid="module-tier-upgrade-btn"
            >
              Upgrade Membership
            </Button>
          </CardContent>
        </Card>
      )}

      {gates?.baseline && gates.baseline.status !== 'completed' && !adminBypass ? (
        <BaselineGate
          gate={gates.baseline}
          canBypass={!!gates.can_bypass}
          onBypass={() => {
            setAdminBypass(true);
            // Recorded as a skip, never a completion, so nobody's progress looks finished.
            axios.post(`${API}/assessments/module/${moduleId}/skip-baseline`, {}, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            }).catch(() => {});
          }}
        />
      ) : folders.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500">This module has no folders yet. Check back soon!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {folders.map((folder, i) => renderFolder(folder, i === folders.length - 1))}
        </div>
      )}

      <TierPaymentModal
        isOpen={showTierPaymentModal}
        onClose={() => setShowTierPaymentModal(false)}
        onSuccess={() => { setShowTierPaymentModal(false); fetchModuleData(); }}
        currentTier={membershipTier}
      />

      <DripPacingOffer
        open={showDripOffer}
        tier={membershipTier}
        onClose={() => setShowDripOffer(false)}
        onPurchased={fetchModuleData}
      />
    </div>
  );
};

export default ModuleDetailPage;
